import {
    OrganizationListItem,
    OrganizationListResponse,
} from "@gsx/common/src/types/http/dashboard/organization";
import { Module, RegisterOptions } from "vuex-class-modules";
import ListStoreVuex from "../../../../common/src/stores/general/ListStoreVuex";
import { Api } from "../../../../common/src/api/Api";
import { OrganizationFilterStore } from "./OrganizationFilterStore";
import { operators } from "../../utils/utils";
import { FilterableListStore } from "../abstracts/CollectionStore";

@Module({ generateMutationSetters: true })
export class OrganizationListStore
    extends ListStoreVuex<OrganizationListItem>
    implements FilterableListStore<OrganizationListItem>
{
    query: string = "";
    onlyCoordinates: boolean = false;

    constructor(
        private readonly api: Api,
        private readonly organizationFilterStore: OrganizationFilterStore,
        options: RegisterOptions,
    ) {
        super(options);
        this.pageSize = Number.MAX_SAFE_INTEGER;
    }

    get filteredItems(): OrganizationListItem[] {
        const organizations = this.items || [];

        if (!organizations) {
            return [];
        }

        return organizations.filter((organization) => {
            const products = new Set(organization.productTags);
            const services = new Set(organization.serviceTags);
            const operatingRegimes = new Set(organization.operatingRegimeTags);
            const applicationTags = new Set(organization.applicationTags);

            return operators[this.organizationFilterStore.logicalOperator](
                {
                    list: [...this.organizationFilterStore.states],
                    comparison: (state: string) => state === `state:${organization.state}`,
                },
                {
                    list: [...this.organizationFilterStore.countries],
                    comparison: (country: string) => country === `country:${organization.country}`,
                },
                {
                    list: [...this.organizationFilterStore.productTags],
                    comparison: products.has.bind(products),
                },
                {
                    list: [...this.organizationFilterStore.serviceTags],
                    comparison: services.has.bind(services),
                },
                {
                    list: [...this.organizationFilterStore.operatingRegimeTags],
                    comparison: operatingRegimes.has.bind(operatingRegimes),
                },
                {
                    list: [...this.organizationFilterStore.applicationTags],
                    comparison: applicationTags.has.bind(applicationTags),
                },
                {
                    list: this.organizationFilterStore.afterYear
                        ? [this.organizationFilterStore.afterYear]
                        : [],
                    comparison: (item) => organization.dateYearFounded! >= item,
                },
                {
                    list: this.organizationFilterStore.beforeYear
                        ? [this.organizationFilterStore.beforeYear]
                        : [],
                    comparison: (item) => organization.dateYearFounded! <= item,
                },
            );
        });
    }

    protected async fetch(): Promise<OrganizationListResponse> {
        return this.api.dashboard.organization.list({
            ...this.queryParams,
            query: this.query,
            applicationTags: Array.from(this.organizationFilterStore.applicationTags),
            operatingRegimeTags: Array.from(this.organizationFilterStore.operatingRegimeTags),
            productTags: Array.from(this.organizationFilterStore.productTags),
            serviceTags: Array.from(this.organizationFilterStore.serviceTags),
            countries: Array.from(this.organizationFilterStore.countries),
            states: Array.from(this.organizationFilterStore.states),
            logicalOperator: this.organizationFilterStore.logicalOperator,
            afterYear: this.organizationFilterStore.afterYear,
            beforeYear: this.organizationFilterStore.beforeYear,
            onlyCoordinates: this.onlyCoordinates,
        });
    }
}
